import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { spaceCategories } from "@/components/categories";

export const Route = createFileRoute("/categories")({
  head: () => ({
    meta: [
      { title: "All Categories — Spayzo" },
      { name: "description", content: "Browse every type of workspace on Spayzo: shared offices, private offices, meeting rooms, coworking spaces, and more." },
      { property: "og:title", content: "All Categories — Spayzo" },
      { property: "og:description", content: "Browse every type of workspace on Spayzo." },
    ],
    links: [{ rel: "canonical", href: "https://spayzo.lovable.app/categories" }],
  }),
  component: CategoriesPage,
});

function CategoriesPage() {
  const { t, i18n } = useTranslation();
  const nf = new Intl.NumberFormat(i18n.language === "ar" ? "ar-SA" : "en-US");
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="max-w-2xl">
          <div className="text-xs font-semibold uppercase tracking-widest text-primary">{t("categoriesPage.eyebrow")}</div>
          <h1 className="mt-3 font-display text-4xl font-bold tracking-tight sm:text-5xl">
            {t("categoriesPage.title")}
          </h1>
          <p className="mt-4 text-muted-foreground">{t("categoriesPage.subtitle")}</p>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {spaceCategories.map((cat) => (
            <Link
              key={cat.slug}
              to="/discover"
              search={{ category: cat.slug } as never}
              className="group relative overflow-hidden rounded-3xl border border-border bg-card shadow-soft transition-all hover:-translate-y-1 hover:shadow-elevated"
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src={cat.image}
                  alt={t(`categories.items.${cat.slug}`)}
                  width={800}
                  height={1000}
                  loading="lazy"
                decoding="async"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              </div>

              <div className="absolute start-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-card/95 text-primary shadow-soft backdrop-blur">
                <cat.icon className="h-4.5 w-4.5" />
              </div>

              <div className="absolute inset-x-4 bottom-4 flex items-end justify-between text-white">
                <div>
                  <div className="font-display text-xl font-bold">{t(`categories.items.${cat.slug}`)}</div>
                  <div className="text-xs text-white/80">{nf.format(cat.count)} {t("categories.spaces")}</div>
                </div>
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur transition-all group-hover:bg-white group-hover:text-foreground">
                  <ArrowUpRight className="h-4 w-4 rtl:-scale-x-100" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
